/* eslint-disable react/jsx-props-no-spreading */

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';

import { Button } from '@sede-enterprise/shell-ds-react-framework';

import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';

import FormControlDatePicker from '../../../../../UI/FormComponent/FormControlDatePicker';
import FormControlSelect from '../../../../../UI/FormComponent/FormControlSelect';
import FormControlTextArea from '../../../../../UI/FormComponent/FormControlTextArea';
import FormControlTextInput from '../../../../../UI/FormComponent/FormControlTextInput';
import { Col, FormRow } from '../../../../../UI/Grid';
import { Modal } from '../../../../../UI/Modal';
import {
  ADD_UPDATE_SWING_CONTRACT,
  SWING_CONTRACT_DESTINATIONS,
  SWING_CONTRACT_MESSAGES,
  SWING_CONTRACT_SOURCES,
  DEAL_UNIT_ENUM,
} from '../../../../../constants/FieldNomsSwingContract';
import useGetAllContractNames from '../../../../../hooks/fieldNomsSwingContract/Mapping/useGetAllContractNames';
import useGetAllSwingContractType from '../../../../../hooks/fieldNomsSwingContract/Mapping/useGetAllSwingContractType';
import useSwingContractEdit from '../../../../../hooks/fieldNomsSwingContract/Mapping/useSwingContractEdit';
import { post } from '../../../../../services/GenesisApi';
import { SwingContractMapping } from '../../../../../types/FieldNomsSwingContract.type';
import DATEFORMAT from '../../../../../utils/dateFormat';
import { ModalFooter } from '../style';
import { FormProviderWrapper, FormSection, FormWrapper } from './style';
import { sendNotification } from '../../../../../UI/Notification';

interface IAddEditTerminalMapping {
  open: boolean;
  onClose: () => void;
  selectedItem: SwingContractMapping | null;
  refreshData: () => void;
}

const DESTINATION_ALIGNE = 'Aligne';
const DESTINATION_ENDUR = 'Endur';

const AddEditZKeyMapping: React.FC<IAddEditTerminalMapping> = ({
  open = false,
  onClose = () => {},
  selectedItem,
  refreshData = () => {},
}) => {
  const methods = useForm();
  const {
    reset,
    formState: { isDirty },
    watch,
    setValue,
  } = methods;

  const validToDate = watch('validTo');
  const validFromDate = watch('validFrom');
  const selectedDestination = watch('destination');

  const [loading, setLoading] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [alreadyExists, setAlreadyExists] = useState<{
    error: boolean;
    message: string;
  }>({
    error: false,
    message: '',
  });
  const { contractNames, loading: contractLoading } = useGetAllContractNames();

  const { swingContractTypes, loading: swingLoading } =
    useGetAllSwingContractType();

  const { loading: mappingDataLoading, fetchAndPopulateData } =
    useSwingContractEdit({ reset });

  // Determine if Aligne or Endur is selected
  const isAligneSelected = selectedDestination === DESTINATION_ALIGNE;
  const isEndurSelected = selectedDestination === DESTINATION_ENDUR;

  useEffect(() => {
    if (
      validFromDate &&
      validToDate &&
      dayjs(validFromDate).isAfter(dayjs(validToDate))
    ) {
      reset((prevValues) => ({
        ...prevValues,
        validFrom: validToDate,
        validTo: null,
      }));
    } else if (!validFromDate) {
      reset((prevValues) => ({
        ...prevValues,
        validTo: null,
      }));
    }
  }, [validFromDate, validToDate, reset]);

  useEffect(() => {
    if (selectedItem && open) {
      setIsInitialLoad(true);
      fetchAndPopulateData(selectedItem).then(() => {
        setTimeout(() => setIsInitialLoad(false), 100);
      });
    } else {
      setIsInitialLoad(false);
    }
  }, [selectedItem, open, fetchAndPopulateData]);

  // Clear the other field when destination changes
  useEffect(() => {
    if (isInitialLoad) {
      return;
    }

    if (isAligneSelected) {
      setValue('deal_DealLegId', '');
    } else if (isEndurSelected) {
      setValue('zKey', '');
    }
  }, [
    selectedDestination,
    setValue,
    isAligneSelected,
    isEndurSelected,
    isInitialLoad,
  ]);

  const handleOnClose = () => {
    setAlreadyExists({
      error: false,
      message: '',
    });
    setIsInitialLoad(true);
    reset({
      deal_DealLegId: '',
      dealUnit: 'Therms',
      zKey: '',
      contractNameId: null,
      entryOrExitId: null,
      description: '',
      locationOrExitMeter: '',
      geminiActivityNumber: '',
      validFrom: null,
      validTo: null,
      destination: null,
      source: null,
    });
    onClose();
  };

  const handleOnSubmit = (formData) => {
    setLoading(true);
    let payload = formData;

    if (selectedItem) {
      payload = {
        ...payload,
        id: selectedItem?.id,
        validFrom: dayjs(formData.validFrom).format(DATEFORMAT.yearMonthDay),
        validTo: dayjs(formData.validTo).format(DATEFORMAT.yearMonthDay),
        source: formData.source ?? '',
      };
    } else {
      payload = {
        ...payload,
        id: 0,
      };
    }

    post(ADD_UPDATE_SWING_CONTRACT, {
      ...payload,
      validFrom: dayjs(formData.validFrom).format(DATEFORMAT.yearMonthDay),
      validTo: dayjs(formData.validTo).format(DATEFORMAT.yearMonthDay),
    })
      .then(() => {
        sendNotification({
          type: 'success',
          message: `Mapping ${
            selectedItem ? 'Updated' : 'Created'
          } Successfully!`,
        });
        handleOnClose();
        refreshData();
      })
      .catch((err) => {
        setAlreadyExists({
          error: true,
          message: err as string,
        });
        sendNotification({
          type: 'error',
          message: err as string,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderFooter = () => (
    <ModalFooter>
      <Button
        onClick={methods.handleSubmit(handleOnSubmit)}
        loading={loading || mappingDataLoading}
        disabled={loading || mappingDataLoading || !isDirty}
      >
        {selectedItem ? 'Update' : 'Save'}
      </Button>
      <Button variant='outlined' onClick={handleOnClose}>
        Cancel
      </Button>
    </ModalFooter>
  );

  const renderIdentifierField = () => {
    // If Endur is selected, show Deal_DealLegId
    if (isEndurSelected) {
      return (
        <Col size={6}>
          <FormControlTextInput
            controllerProps={{
              name: 'deal_DealLegId',
              rules: {
                required: SWING_CONTRACT_MESSAGES.REQUIRED_DEAL_LEG_ID,
                pattern: {
                  value: /^\d+_\d+$/,
                  message: SWING_CONTRACT_MESSAGES.INVALID_DEAL_LEG_ID,
                },
              },
            }}
            label='Deal ID'
            data-testid='deal_DealLegId'
            methods={methods}
          />
        </Col>
      );
    }

    // If Aligne is selected, show Zkey
    if (isAligneSelected) {
      return (
        <Col size={6}>
          <FormControlTextInput
            controllerProps={{
              name: 'zKey',
              rules: {
                required:
                  SWING_CONTRACT_MESSAGES.REQUIRED_ZKEY || 'Zkey is required',
              },
            }}
            label='ZKey'
            data-testid='zKey'
            methods={methods}
          />
        </Col>
      );
    }

    // If no destination is selected yet, show a disabled placeholder
    return (
      <Col size={6}>
        <FormControlTextInput
          controllerProps={{
            name: 'identifier_placeholder',
            rules: {
              required: false,
            },
          }}
          label='Identifier'
          data-testid='identifier_placeholder'
          methods={methods}
          inputProps={{
            disabled: true,
            placeholder: 'Select destination first',
          }}
        />
      </Col>
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleOnClose}
      title={`${selectedItem ? 'Edit' : 'Add New'} Mapping`}
      isDraggable
      maskClosable={false}
      footer={renderFooter()}
      width='60%'
    >
      <FormProviderWrapper>
        {mappingDataLoading && selectedItem ? (
          <div className='loading-message'>Loading mapping data...</div>
        ) : (
          <FormWrapper>
            <FormProvider {...methods}>
              <FormSection>
                <h3>Contract Details</h3>
                <FormRow>
                  <Col size={6}>
                    <FormControlSelect
                      controllerProps={{
                        name: 'contractNameId',
                        rules: {
                          required:
                            SWING_CONTRACT_MESSAGES.REQUIRED_CONTRACT_NAME,
                        },
                      }}
                      label='Contract Name'
                      data-testid='contractNameId'
                      methods={methods}
                      data={contractNames}
                      isLoading={false}
                      selectProps={{
                        loading: contractLoading,
                      }}
                    />
                  </Col>
                  <Col size={6}>
                    <FormControlSelect
                      controllerProps={{
                        name: 'entryOrExitId',
                        rules: {
                          required:
                            SWING_CONTRACT_MESSAGES.REQUIRED_ENTRY_OR_EXIT,
                        },
                      }}
                      label='Entry/Exit'
                      data-testid='entryOrExitId'
                      methods={methods}
                      data={swingContractTypes}
                      isLoading={false}
                      selectProps={{
                        loading: swingLoading,
                      }}
                    />
                  </Col>
                </FormRow>

                <FormRow>
                  <Col size={6}>
                    <FormControlTextInput
                      controllerProps={{
                        name: 'locationOrExitMeter',
                        rules: {
                          required: false,
                        },
                      }}
                      label='Location/Exit Meter'
                      data-testid='locationOrExitMeter'
                      methods={methods}
                    />
                  </Col>
                  <Col size={6}>
                    <FormControlTextInput
                      controllerProps={{
                        name: 'geminiActivityNumber',
                        rules: {
                          required: false,
                        },
                      }}
                      label='Gemini Activity Number'
                      data-testid='geminiActivityNumber'
                      methods={methods}
                    />
                  </Col>
                </FormRow>
              </FormSection>

              <FormSection>
                <h3>Source & Destination</h3>
                <FormRow>
                  <Col size={6}>
                    <FormControlSelect
                      controllerProps={{
                        name: 'source',
                        rules: {
                          required: SWING_CONTRACT_MESSAGES.REQUIRED_SOURCE,
                        },
                      }}
                      isLoading={false}
                      label='Source'
                      data-testid='source'
                      methods={methods}
                      data={SWING_CONTRACT_SOURCES}
                    />
                  </Col>
                  <Col size={6}>
                    <FormControlSelect
                      controllerProps={{
                        name: 'destination',
                        rules: {
                          required:
                            SWING_CONTRACT_MESSAGES.REQUIRED_DESTINATION,
                        },
                      }}
                      isLoading={false}
                      label='Destination'
                      data-testid='destination'
                      methods={methods}
                      data={SWING_CONTRACT_DESTINATIONS}
                    />
                  </Col>
                </FormRow>
                <FormRow>
                  {renderIdentifierField()}
                  <Col size={6}>
                    <FormControlSelect
                      controllerProps={{
                        name: 'dealUnit',
                        rules: {
                          required: SWING_CONTRACT_MESSAGES.REQUIRED_DEAL_UNIT,
                        },
                      }}
                      label='Deal Unit'
                      isLoading={false}
                      data={DEAL_UNIT_ENUM}
                      methods={methods}
                      data-testid='dealUnit'
                      selectProps={{ allowClear: false }}
                    />
                  </Col>
                </FormRow>
              </FormSection>

              <FormSection>
                <h3>Validity Period</h3>
                <FormRow>
                  <Col size={6}>
                    <FormControlDatePicker
                      controllerProps={{
                        name: 'validFrom',
                        rules: {
                          required: 'Valid From Date is required',
                        },
                      }}
                      label='Valid From'
                      methods={methods}
                      data-testid='validFrom'
                      inputProps={{
                        placeholder: 'DD MM YYYY',
                      }}
                    />
                  </Col>
                  <Col size={6}>
                    <FormControlDatePicker
                      controllerProps={{
                        name: 'validTo',
                        rules: {
                          required: 'Valid To Date is required',
                        },
                      }}
                      label='Valid To'
                      methods={methods}
                      data-testid='validTo'
                      inputProps={{
                        placeholder: 'DD MM YYYY',
                        disabled: !watch('validFrom'),
                        disabledDate: (current) => {
                          return (
                            current &&
                            dayjs(current).isBefore(watch('validFrom'), 'day')
                          );
                        },
                      }}
                    />
                  </Col>
                </FormRow>
              </FormSection>

              <FormSection>
                <h3>Additional Information</h3>
                <FormRow>
                  <Col size={12}>
                    <FormControlTextArea
                      controllerProps={{
                        name: 'description',
                        rules: {
                          required:
                            SWING_CONTRACT_MESSAGES.REQUIRED_DESCRIPTION,
                        },
                      }}
                      label='Description'
                      data-testid='description'
                      methods={methods}
                    />
                  </Col>
                </FormRow>
              </FormSection>

              {alreadyExists.error && (
                <div className='already-exists'>{`Mapping already exists! ${alreadyExists.message}`}</div>
              )}
            </FormProvider>
          </FormWrapper>
        )}
      </FormProviderWrapper>
    </Modal>
  );
};

export default AddEditZKeyMapping;
